import { createPortal } from 'react-dom'

/**
 * WizardShell — coque commune des wizards producteur (ajouter une offre /
 * proposer un produit). Une decision par ecran : en-tete, progression, corps
 * qui glisse, pied avec le CTA.
 *
 * REMISE A LA FORME DU PROTOTYPE (S66, remonte par Enzo)
 * -----------------------------------------------------
 * C'etait une **boite blanche centree** a coins ronds sur un voile NOIR : la
 * grammaire d'une modale de bureau, pas celle de l'application. Le prototype
 * (`WizardOffre`) en fait une **feuille qui monte du bas** — creme, coins hauts
 * seulement, poignee de prehension, voile vert profond — exactement comme les
 * autres feuilles du portail (`ConfirmStandSheet`, `StandOffersModal`). Sur
 * telephone la difference n'est pas cosmetique : une feuille se pousse au pouce
 * et laisse voir d'ou l'on vient, une boite centree flotte sans attache.
 *
 * Alignements repris du prototype, un par un :
 *  - feuille en bas (`items-end`), 88dvh, coins `28px 28px 0 0`, `slideUp` ;
 *  - fond **creme** (`--c-creme`) et non blanc — le blanc pur n'existe pas dans
 *    la charte ;
 *  - voile **vert profond** flou, pas du noir ;
 *  - **poignee** de 42x4 px, la meme que les autres feuilles ;
 *  - **titre en serif sur la ligne de l'en-tete**, entre la pastille de retour
 *    et la croix — au lieu d'un titre sans-serif sur sa propre ligne ;
 *  - pastilles de verre `rounded-full` pour ← et ✕, non des carres beiges ;
 *  - progression en **segments** (un par etape, orange), non une barre continue
 *    a pourcentage : on voit combien d'ecrans restent, ce qu'un pourcentage ne
 *    dit pas. Le compteur « 1 / 5 » devient donc inutile et disparait.
 *
 * `createPortal` : convention du portail pour toute modale (CLAUDE.md) — la
 * coque ne l'utilisait pas, ce qui l'exposait aux `transform` des parents.
 */
function WizardShell({
  tc,
  ts,
  title,
  subtitle,
  stepIndex,
  totalSteps,
  direction = 'right',
  canBack = true,
  onBack,
  onClose,
  children,
  footer,
}) {
  const slideClass = direction === 'left' ? 'wizard-slide-left' : 'wizard-slide-right'
  const segments = Array.from({ length: Math.max(1, totalSteps) }, (_, i) => i)

  return createPortal(
    <div
      className="fixed inset-0 z-[100] flex items-end"
      role="dialog"
      aria-modal="true"
      style={{
        background: 'rgba(4,25,24,0.45)',
        backdropFilter: 'blur(10px)',
        WebkitBackdropFilter: 'blur(10px)',
      }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full flex flex-col panel-slide-up"
        style={{
          background: 'rgb(var(--c-creme))',
          borderRadius: '28px 28px 0 0',
          height: '88dvh',
        }}
      >
        {/* En-tete fixe : poignee, retour, titre serif, fermeture, progression */}
        <div className="px-[18px] pt-4 pb-2.5 flex-shrink-0">
          <div className="w-[42px] h-1 rounded-full mx-auto mb-3" style={{ background: 'rgba(7,62,61,0.15)' }} />
          <div className="flex items-center gap-2.5">
            {/* Pas de fantome : sur le premier ecran le titre part du bord, comme
                dans le prototype. Un bouton invisible decalait le titre (S68). */}
            {canBack && (
              <button
                onClick={onBack}
                aria-label="Retour"
                className={`panneau-verre rounded-full px-3 py-[7px] text-[13px] font-bold flex-shrink-0 ${tc}`}
              >
                ←
              </button>
            )}
            <p className={`flex-1 min-w-0 font-serif text-[21px] leading-tight truncate ${tc}`}>{title}</p>
            <button
              onClick={onClose}
              aria-label="Fermer"
              className={`panneau-verre rounded-full px-3 py-[7px] text-[13px] font-bold flex-shrink-0 ${tc}`}
            >
              ✕
            </button>
          </div>

          {/* Progression par segments — un par etape (prototype) */}
          {totalSteps > 1 && (
            <div className="flex gap-[5px] mt-3">
              {segments.map((i) => (
                <span
                  key={i}
                  className="flex-1 h-1 rounded-full transition-colors duration-200"
                  style={{ background: i <= stepIndex ? 'rgb(var(--c-primary))' : 'rgba(7,62,61,0.1)' }}
                />
              ))}
            </div>
          )}

          {subtitle && <p className="text-[13px] mt-3 text-ink/65">{subtitle}</p>}
        </div>

        {/* Corps — glisse a chaque changement d'etape. Le CTA vit DANS le corps,
            juste sous le contenu, comme dans le prototype : il n'y a pas de barre
            de pied separee (banc d'essai S68 — la barre a bordure etait un
            reliquat de la boite centree). */}
        <div
          key={`step-${stepIndex}-${direction}`}
          className={`flex-1 overflow-y-auto px-[18px] pt-1.5 ${slideClass}`}
          style={{ paddingBottom: 'max(1.5rem, env(safe-area-inset-bottom))' }}
        >
          {children}
          {footer && <div className="mt-4">{footer}</div>}
        </div>
      </div>
    </div>,
    document.body,
  )
}

export default WizardShell
